import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaUserShield } from 'react-icons/fa';
import { API_BASE } from '../utils/helpers';
import UiModal from '../components/UiModal';

const emptyForm = { id: '', name: '', email: '', password: '', mobile: '', user_type: 'Employee' };

function roleTone(type) {
  switch (type) {
    case 'Admin': return 'bg-indigo-50 text-indigo-700';
    case 'Manager': return 'bg-amber-50 text-amber-700';
    default: return 'bg-slate-100 text-slate-600';
  }
}

const InternalUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/internal-users/all`);
      setUsers(res.data || []);
    } catch (err) {
      console.error('Failed to fetch internal users', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleChange = (e) => setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleCreateOrUpdate = async () => {
    try {
      if (isEditMode) await axios.put(`${API_BASE}/api/internal-users/update/${formData.id}`, formData);
      else await axios.post(`${API_BASE}/api/internal-users/create`, formData);
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      alert(err.response?.data?.message || 'Error saving user');
      console.error('Error saving user', err);
    }
  };

  const handleEdit = (user) => {
    setFormData({ id: user.id, name: user.name, email: user.email, password: '', mobile: user.mobile, user_type: user.user_type });
    setIsEditMode(true);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API_BASE}/api/internal-users/delete/${id}`);
      fetchUsers();
    } catch (err) {
      alert('Failed to delete user');
      console.error(err);
    }
  };

  const resetForm = () => { setFormData(emptyForm); setIsEditMode(false); };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Internal Users</h2>
          <p className="text-sm text-slate-500">Admin, manager and employee accounts.</p>
        </div>
        <button className="ui-btn-primary" onClick={() => { resetForm(); setShowModal(true); }}>
          <FaPlus className="text-xs" /> Add user
        </button>
      </div>

      <div className="ui-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="ui-table">
            <thead>
              <tr>
                <th className="w-12">#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Role</th>
                <th>Created</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={`sk-${i}`}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <td key={j}><div className="h-4 w-full max-w-[110px] animate-pulse rounded bg-slate-100" /></td>
                    ))}
                  </tr>
                ))}

              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={7}>
                    <div className="flex flex-col items-center justify-center gap-2 py-12 text-slate-400">
                      <FaUserShield className="text-2xl" />
                      <p className="text-sm">No internal users yet.</p>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && users.map((user, i) => (
                <tr key={user.id}>
                  <td className="text-slate-400">{i + 1}</td>
                  <td className="font-medium text-slate-900">{user.name}</td>
                  <td className="text-slate-500">{user.email}</td>
                  <td className="text-slate-500">{user.mobile || '—'}</td>
                  <td><span className={`ui-badge ${roleTone(user.user_type)}`}>{user.user_type}</span></td>
                  <td className="whitespace-nowrap text-slate-500">{user.created_at ? new Date(user.created_at).toLocaleString() : '—'}</td>
                  <td>
                    <div className="flex items-center justify-end gap-2">
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs" onClick={() => handleEdit(user)}>
                        <FaEdit className="text-xs" /> Edit
                      </button>
                      <button className="ui-btn-ghost px-2.5 py-1.5 text-xs text-rose-600 hover:bg-rose-50" onClick={() => handleDelete(user.id)}>
                        <FaTrash className="text-xs" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UiModal
        open={showModal}
        title={isEditMode ? 'Edit User' : 'Add New User'}
        onClose={() => setShowModal(false)}
        footer={
          <>
            <button className="ui-btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
            <button className="ui-btn-primary" onClick={handleCreateOrUpdate}>{isEditMode ? 'Update' : 'Create'}</button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
            <input className="ui-input" name="name" value={formData.name} onChange={handleChange} placeholder="Enter full name" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Email</label>
            <input className="ui-input" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="example@email.com" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">
              Password {isEditMode ? <span className="text-slate-400">(leave blank to keep unchanged)</span> : ''}
            </label>
            <input className="ui-input" name="password" type="password" value={formData.password} onChange={handleChange} placeholder={isEditMode ? 'Optional' : 'Required'} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Mobile</label>
            <input className="ui-input" name="mobile" value={formData.mobile} onChange={handleChange} placeholder="Mobile number" />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">User Type</label>
            <select className="ui-input" name="user_type" value={formData.user_type} onChange={handleChange}>
              <option value="Admin">Admin</option>
              <option value="Manager">Manager</option>
              <option value="Employee">Employee</option>
            </select>
          </div>
        </div>
      </UiModal>
    </div>
  );
};

export default InternalUsers;
